import { useState } from 'react'
import { Box, CircularProgress, Paper, styled } from '@mui/material'
import { SaveAlt, Link, PeopleOutlineOutlined } from '@mui/icons-material'
import { MenuList } from '@carto/meridian-ds/components'
import { Publish } from '@carto/meridian-ds/custom-icons'

import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'app/state'
import { ContextMenuItem } from 'features/common/ui/ContextMenu'
import ContextMenuBaseItem from 'features/common/ui/ContextMenu/ContextMenuBaseItem'
import useUserPermissions from 'features/account/state/useUserPermissions'
import copyString from 'features/common/utils/copyString'
import useNotifications from 'features/notifications/ui/useNotifications'
import { createDialogOpener } from 'features/dialogs/openDialog'

import { MapPrivacy } from 'features/maps/types'
import { getExportableLayersInfo, hasExportableLayers } from 'features/builder/model/layerDataExport'
import { setJobsPopperState } from 'features/jobs/state/jobsProcessingSlice'
import { JobsPopperTab } from 'features/jobs/types'
import { mapHasUpdatePending } from 'features/maps/utils'
import { MetricEventType } from 'features/metrics/api/metrics'
import { sendMetricEvent } from 'features/metrics/model/metricsMutations'
import { getMapSharingUrl } from 'features/builder/paths'
import PDFIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { publishAndSetMapPrivacy } from 'features/builder/model/sharing'
import { useAccountGovernanceMaps } from 'features/account/hooks/useAccountGovernanceMaps'

const openExportDataDialog = createDialogOpener(() => import('../../../Export/ExportDataDialog'))
const openPDFCommentsDialog = createDialogOpener(() => import('../../../PDFExport/PDFCommentsDialog'))
const openPDFPreviewDialog = createDialogOpener(() => import('../../../PDFExport/PDFPreviewDialog'))

enum SharedMapActions {
  SharingOption = 'sharingOptions',
  PublishUpdates = 'publishUpdates',
  CopyLink = 'copyLink',
  Export = 'export',
  ExportPDF = 'exportPDF'
}

const Circle = styled('div')(({ theme }) => ({
  width: theme.spacing(1),
  height: theme.spacing(1),
  backgroundColor: theme.palette.secondary.main,
  border: `1px solid ${theme.palette.secondary.main}`,
  borderRadius: '50%'
}))

const MenuItemSvgSizeFix = styled(Box)(({ theme }) => ({
  // MenuItem overrides some svg properties, and breaks CircularProgress, fix it here
  svg: {
    width: theme.spacing(1.5),
    height: theme.spacing(1.5),
    minWidth: theme.spacing(1.5)
  }
}))

function BuilderShareActionsMenu({
  onDismiss,
  viewOnly,
  sharingDisabled,
  onOpenSharingSettings
}: {
  viewOnly?: boolean
  sharingDisabled?: boolean
  onDismiss: () => void
  onOpenSharingSettings: () => void
}) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const currentMap = useAppSelector((state) => state.maps.currentMap)
  const currentMapDatasets = useAppSelector((state) => state.maps.currentMapDatasets)
  const mapSettings = useAppSelector((state) => state.maps.mapSettings)
  const { data: governanceMaps } = useAccountGovernanceMaps()
  const { sendSuccessNotificationLocalize, sendErrorNotificationLocalize, sendNeutralNotificationLocalize } =
    useNotifications()

  const publishMapUpdatePending = currentMap && mapHasUpdatePending(currentMap)

  const { exportViewportDataAvailable, exportPdfReportAvailable } = useUserPermissions()
  const showExportData =
    (!viewOnly || mapSettings?.exportViewportData) &&
    governanceMaps?.allowUsersToManageViewportDataExport &&
    exportViewportDataAvailable
  const noExportableLayers = !useAppSelector(hasExportableLayers)
  const exportableLayersInfo = useAppSelector(getExportableLayersInfo)
  const noDatasets = currentMapDatasets.length === 0
  const disableExport = noDatasets || noExportableLayers

  const showExportPDF =
    (!viewOnly || mapSettings?.exportPDF) && governanceMaps?.allowUsersToManagePDFReports && exportPdfReportAvailable
  const disableExportPDF = noDatasets

  const isPrivateMap = currentMap?.privacy === MapPrivacy.Private
  const [isPublishing, setIsPublishing] = useState(false)

  const getDisabledExportTooltipId = () => {
    if (noDatasets) {
      return 'builder.actions.export.noDatasetsTooltip'
    }
    if (exportableLayersInfo.some(({ unexportableReason }) => unexportableReason === 'unsupported-export-method')) {
      return 'builder.actions.export.noExportableLayersTooltip'
    }
    if (exportableLayersInfo.some(({ unexportableReason }) => unexportableReason === 'inactive-in-legend')) {
      return 'builder.actions.export.noExportableLayersDueToLegendTooltip'
    }
    return undefined
  }

  const actions: ContextMenuItem[] = [
    {
      id: SharedMapActions.SharingOption,
      label: intl.formatMessage({ id: 'builder.actions.sharingOptions' }),
      startAdornment: <PeopleOutlineOutlined />,
      disabled: sharingDisabled
    },
    {
      id: SharedMapActions.PublishUpdates,
      disabled: isPrivateMap,
      label: intl.formatMessage({ id: 'builder.actions.publishUpdates' }),
      startAdornment: <Publish />,
      endAdornment: isPublishing ? (
        <MenuItemSvgSizeFix>
          <CircularProgress size="12px" aria-label="publishing" />
        </MenuItemSvgSizeFix>
      ) : publishMapUpdatePending ? (
        <Circle aria-label="update-pending" />
      ) : undefined,
      tooltipId: isPrivateMap ? 'builder.shareDialog.copyDisabledTooltip' : undefined
    },
    {
      id: SharedMapActions.CopyLink,
      disabled: isPrivateMap,
      label: intl.formatMessage({ id: 'builder.actions.copyLink' }),
      startAdornment: <Link />,
      tooltipId: isPrivateMap ? 'builder.shareDialog.copyDisabledTooltip' : undefined
    },
    {
      divider: true,
      hidden: !showExportData,
      id: SharedMapActions.Export,
      label: intl.formatMessage({ id: 'builder.actions.export.title' }),
      startAdornment: <SaveAlt />,
      disabled: disableExport,
      tooltipId: disableExport ? getDisabledExportTooltipId() : undefined
    },
    {
      hidden: !showExportPDF,
      id: SharedMapActions.ExportPDF,
      label: intl.formatMessage({ id: 'builder.exportPDF.title' }),
      startAdornment: <PDFIcon />,
      disabled: disableExportPDF
    }
  ]

  const handleStartedExport = () => {
    dispatch(setJobsPopperState({ open: true, tab: JobsPopperTab.Exports }))
  }

  const handlePDFCommentsContinue = (data) => {
    openPDFPreviewDialog({ data, includeHeader: true })
  }

  const handleClickOption = async (item: ContextMenuItem) => {
    if (!currentMap) return

    if (item.id === SharedMapActions.PublishUpdates) {
      const mapId = currentMap.id
      const privacy = currentMap.privacy
      const sharingScope = currentMap.sharingScope
      if (!privacy) return

      sendMetricEvent(MetricEventType.MapPublishUpdate, { mapId, type: 'quick_action' })
      setIsPublishing(true)
      try {
        await publishAndSetMapPrivacy({
          mapId,
          privacy,
          sharingScope: privacy === MapPrivacy.Shared ? sharingScope : undefined
        })
        sendSuccessNotificationLocalize('builder.mapPublishing.updatedSuccessfully')
      } catch (error) {
        console.error('failed to publish map', error)
        sendErrorNotificationLocalize('builder.mapPublishing.error', { error })
      } finally {
        onDismiss()
        setIsPublishing(false)
      }
    } else if (item.id === SharedMapActions.SharingOption) {
      onDismiss()
      onOpenSharingSettings()
    } else if (item.id === SharedMapActions.CopyLink) {
      if (!currentMap || currentMap.privacy === MapPrivacy.Private) return

      sendMetricEvent(MetricEventType.CopyMapLink, { mapId: currentMap.id, type: 'quick_action' })
      const mapUrl = getMapSharingUrl(currentMap)

      copyString(mapUrl).then(() => {
        sendNeutralNotificationLocalize('builder.actions.linkCopiedToClipboard', {
          autoWidth: true,
          closeable: false
        })
      })
      onDismiss()
    } else if (item.id === SharedMapActions.Export) {
      openExportDataDialog({ onStartedExport: handleStartedExport })
      onDismiss()
    } else if (item.id === SharedMapActions.ExportPDF) {
      openPDFCommentsDialog({ onContinue: handlePDFCommentsContinue })
    }
  }

  const clickItem = (item: ContextMenuItem) => {
    handleClickOption(item)
  }

  return (
    <Paper>
      <MenuList>
        {actions
          .filter((c) => !c.hidden)
          .map((item, index) => (
            <ContextMenuBaseItem key={index} clickItem={clickItem} item={item} />
          ))}
      </MenuList>
    </Paper>
  )
}

export default BuilderShareActionsMenu
